/**
 * Kết quả tìm kiếm BĐS trên trang chủ — layout lưới + bản đồ khu vực.
 *
 * Luồng dữ liệu:
 * - usePropertyList đọc query string URL, fetch danh sách có phân trang.
 * - propertyApi.getPropertyAreas lấy dữ liệu marker cho AreaSearchMap (bỏ district/ward/page).
 * - handleAreaSelect cập nhật URL province/ward → trigger lại cả list và map.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AreaSearchMap } from './AreaSearchMap';
import { EmptyState } from '../Common/EmptyState';
import { InlineLoadingIndicator } from '../Common/InlineLoadingIndicator';
import { Pagination } from '../Common/Pagination';
import { PropertyCard } from '../property/PropertyCard';
import { PropertyCardSkeletonGrid } from '../property/PropertyCardSkeleton';
import { usePropertyList } from '../../features/properties/hooks/usePropertyList';
import { propertyApi } from '../../features/properties/api/propertyApi';
import { APP_ROUTES } from '../../shared/constants/routes';
import { formatText, TEXT } from '../../shared/languages';
import { getBrowseSectionHeading } from '../../shared/utils/browseSectionSearch';
import { parseLocationSearchFilters } from '../../shared/utils/searchMapLocation';

export const HomeSearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  /** Dữ liệu nhóm khu vực cho bản đồ bên phải */
  const [areaMapItems, setAreaMapItems] = useState([]);
  const {
    propertyList,
    pagination,
    isInitialLoading,
    isFetchingProperties,
    errorMessage,
    shouldScrollToResults,
    resetSearchFilters,
    changePage,
    clearScrollRequest,
    retryLoadProperties,
  } = usePropertyList();
  const t = TEXT.property.list;
  const home = TEXT.home;
  const common = TEXT.common;
  const sectionHeading = getBrowseSectionHeading(searchParams);
  const resultCount = pagination?.total || propertyList.length || 0;

  /** Bộ lọc vị trí hiện tại — dùng để highlight marker trên map */
  const locationSearch = useMemo(() => parseLocationSearchFilters(searchParams), [searchParams]);

  /**
   * Key fetch bản đồ: loại district/ward/page để lấy tổng hợp theo tỉnh,
   * tránh refetch không cần thiết khi chỉ đổi trang.
   */
  const areaMapSearchKey = useMemo(() => {
    const areaMapSearchParams = new URLSearchParams(searchParams);

    areaMapSearchParams.delete('district');
    areaMapSearchParams.delete('ward');
    areaMapSearchParams.delete('page');

    return areaMapSearchParams.toString();
  }, [searchParams]);

  /** Khi chọn khu vực trên map → cập nhật province/ward trong URL và reset về trang 1 */
  const handleAreaSelect = useCallback((areaGroup) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.set('province', areaGroup.province);
    nextSearchParams.delete('district');

    if (areaGroup.ward) {
      nextSearchParams.set('ward', areaGroup.ward);
    } else {
      nextSearchParams.delete('ward');
    }

    nextSearchParams.set('page', '1');
    setSearchParams(nextSearchParams, { replace: false });
  }, [searchParams, setSearchParams]);

  /** Cuộn lên đầu khi hook báo cần scroll (sau submit tìm kiếm hoặc đổi trang) */
  useEffect(() => {
    if (!shouldScrollToResults) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    clearScrollRequest();
  }, [clearScrollRequest, shouldScrollToResults]);

  /** Tải dữ liệu khu vực cho bản đồ khi bộ lọc tìm kiếm thay đổi */
  useEffect(() => {
    let isMounted = true;
    const areaMapSearchParams = new URLSearchParams(areaMapSearchKey);

    propertyApi
      .getPropertyAreas(Object.fromEntries(areaMapSearchParams.entries()))
      .then((response) => {
        if (isMounted) {
          setAreaMapItems(response.data.data || []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAreaMapItems([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [areaMapSearchKey]);

  return (
    <section className="home-search-results">
      {isFetchingProperties && (
        <InlineLoadingIndicator overlay label={TEXT.messages.searchingProperties} />
      )}

      <div className="home-search-results-layout">
        <div className="home-search-results-list">
          <div className="home-search-results-head">
            <nav className="home-search-results-breadcrumb" aria-label="Breadcrumb">
              <Link to={APP_ROUTES.home}>{common.home}</Link>
              <span aria-hidden="true">/</span>
              <span>{sectionHeading || t.breadcrumb}</span>
            </nav>

            <h1 className="home-search-results-title">
              {sectionHeading ||
                formatText(t.resultSummary, {
                  count: resultCount,
                })}
            </h1>

            {sectionHeading && (
              <p className="home-search-results-subtitle">
                {formatText(t.resultCountLabel, { count: resultCount })}
              </p>
            )}
          </div>

          {errorMessage && (
            <div className="error-box action-box">
              <span>{errorMessage}</span>
              <button className="button ghost" type="button" onClick={retryLoadProperties}>
                {common.tryAgain}
              </button>
            </div>
          )}

          {isInitialLoading && !propertyList.length ? (
            <PropertyCardSkeletonGrid count={8} />
          ) : propertyList.length ? (
            <>
              <div className="home-search-results-grid">
                {propertyList.map((property) => (
                  <div key={property.id} id={`property-card-${property.id}`} className="home-search-results-card">
                    <PropertyCard property={property} />
                  </div>
                ))}
              </div>
              <Pagination
                pagination={pagination}
                isLoading={isFetchingProperties}
                onPageChange={changePage}
              />
            </>
          ) : isFetchingProperties ? null : errorMessage ? null : (
            <EmptyState
              title={t.emptyTitle}
              message={home.emptySectionMessage}
              action={
                <button className="button" type="button" onClick={resetSearchFilters}>
                  {common.resetFilters}
                </button>
              }
            />
          )}
        </div>

        <aside className="home-search-results-map-panel" aria-label={t.areaMapTitle}>
          <AreaSearchMap
            areas={areaMapItems}
            locationSearch={locationSearch}
            onAreaSelect={handleAreaSelect}
          />
        </aside>
      </div>
    </section>
  );
};
